import { useWatch } from 'react-hook-form';
import PricingFormFooter from '@/pages/pricing-forms/PricingFormFooter';
import { PricingConfigurationValues } from '@/services/Pricing/pricing-configuration.service';
import { FormRow, formRowsToValues } from './pricingFormMapping';
import { normalizeToFormPrecision, pricingValuesEqual } from './pricingConfigUtils';

type Props = {
  baseline: PricingConfigurationValues;
  // Currently-stored overrides for this entity. Used as the dirty-check reference so a
  // value equal to the default can still be saved (it clears an existing override).
  existing?: PricingConfigurationValues;
  isSaving: boolean;
  isReadOnly: boolean;
  activeCountry: string;
};

// Enables save when the active country's edited values differ from what is currently
// stored — the existing override if one exists, otherwise the inherited baseline. This
// lets an org reset a custom override back to the default (which removes the override)
// while still disabling no-op saves when nothing changed.
const PricingDiffFooter = ({ baseline, existing, isSaving, isReadOnly, activeCountry }: Props) => {
  const rows = (useWatch({ name: 'rows' }) ?? []) as FormRow[];
  const values = formRowsToValues(rows);
  const normalizedBaseline = normalizeToFormPrecision(baseline);
  const normalizedExisting = existing ? normalizeToFormPrecision(existing) : undefined;
  const activeValues = activeCountry ? values[activeCountry] : undefined;
  const activeStored = activeCountry
    ? (normalizedExisting?.[activeCountry] ?? normalizedBaseline[activeCountry])
    : undefined;
  const canSave =
    !!activeValues && (!activeStored || !pricingValuesEqual(activeValues, activeStored));
  return <PricingFormFooter isUpdating={isSaving} isReadOnly={isReadOnly} canSave={canSave} />;
};

export default PricingDiffFooter;
